import { Box, Button, Typography } from '@mui/material';
import { Image, Link, SpeakerHigh } from '@phosphor-icons/react';
import { memo, useEffect, useRef } from 'react';

export interface LinkComponentProps {
  href?: string;
  children?: React.ReactNode;
  autoPlay?: boolean;
  isNewMessage?: boolean;
  id?: string;
  chatbot?: any;
  messageContent?: string;
  onOpenLinksFlyout?: (content: string, type: 'image' | 'audio' | 'link', href?: string) => void;
  [key: string]: any;
}

// Create a global audio queue manager
const audioQueueManager = {
  queue: [] as string[],
  isPlaying: false,
  currentIndex: 0,
  currentAudio: null as HTMLAudioElement | null,
  processedAudios: new Set<string>(),

  addToQueue(audioSrc: string) {
    if (!this.processedAudios.has(audioSrc)) {
      this.queue.push(audioSrc);
      this.processedAudios.add(audioSrc);

      if (!this.isPlaying) {
        this.playNext();
      }
    }
  },

  playNext() {
    if (this.currentIndex >= this.queue.length) {
      this.stop();
      return;
    }

    this.isPlaying = true;

    const audioElement = document.querySelector(
      `audio[data-id="${this.queue[this.currentIndex]}"]`,
    ) as HTMLAudioElement;

    if (audioElement) {
      this.currentAudio = audioElement;

      // Remove any existing event listeners
      audioElement.onended = null;
      audioElement.onplay = null;
      audioElement.onpause = null;

      // Add event listeners for user interaction
      audioElement.onplay = () => {
        if (this.currentIndex > 0) {
          this.stop();
        }
      };

      audioElement.onpause = () => {
        this.stop();
      };

      audioElement.onended = () => {
        this.currentIndex++;
        this.playNext();
      };

      audioElement.play().catch(error => {
        this.currentIndex++;
        this.playNext();
      });
    } else {
      this.currentIndex++;

      this.playNext();
    }
  },

  stop() {
    if (this.currentAudio?.ended) {
      this.currentIndex++;
      this.playNext();
      return;
    }

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    this.queue = [];
    this.isPlaying = false;
    this.currentIndex = 0;
    this.currentAudio = null;
    this.processedAudios.clear();
  },
};

const LinkComponent = memo(function LinkComponent({
  autoPlay = false,
  isNewMessage = false,
  chatbot,
  messageContent,
  onOpenLinksFlyout,
  ...props
}: Readonly<LinkComponentProps>) {
  const isImageLink = props.href?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
  const isAudioLink = props.href?.match(/\.(mp3|wav|ogg|m4a)$/i);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (isAudioLink && autoPlay && isNewMessage) {
      audioQueueManager.addToQueue(`${props.href}-${props.id}`);
    }
  }, [isAudioLink, autoPlay, isNewMessage, props.href, props.id]);

  const getSiteNameFromUrl = (url: string | undefined): string => {
    if (!url) return 'Link';
    
    try {
      const urlWithProtocol = url.startsWith('http://') || url.startsWith('https://') 
        ? url 
        : `https://${url}`;
      
      const urlObj = new URL(urlWithProtocol);
      let hostname = urlObj.hostname.replace('www.', '');
      
      const siteName = hostname.split('.')[0];
      
      const capitalizedName = siteName.charAt(0).toUpperCase() + siteName.slice(1);
      
      return capitalizedName;
    } catch (e) {
      try {
        const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s\.]+)/i);
        if (match && match[1]) {
          const siteName = match[1];
          const capitalizedName = siteName.charAt(0).toUpperCase() + siteName.slice(1);
          return capitalizedName;
        }
      } catch (err) {
        return props.children?.toString() || 'Link';
      }
      return props.children?.toString() || 'Link';
    }
  };

  const shouldShowButton =
    chatbot?.websites &&
    !isImageLink &&
    !isAudioLink &&
    messageContent &&
    chatbot?.enableChatFlyout &&
    onOpenLinksFlyout;

  if (shouldShowButton) {
    const siteName = getSiteNameFromUrl(props.href);
    
    return (
      <Button
        variant="outlined"
        onClick={() => {
          if (onOpenLinksFlyout && messageContent) {
            onOpenLinksFlyout(messageContent, 'link', props.href);
          }
        }}
        sx={{
          padding: '0px 4px',
          minHeight: '0px',
          borderRadius: '16px',
          borderColor: chatbot?.brandColor || '#3650DE',
          color: chatbot?.brandColor || '#3650DE',
          '&:hover': {
            borderColor: chatbot?.brandColor || '#3650DE',
            backgroundColor: chatbot?.brandColor
              ? `${chatbot.brandColor}15`
              : '#3650DE15',
          },
          width: 'fit-content',
          gap: '5px',
        }}
      >
        <Link size={12} color={chatbot?.brandColor || '#3650DE'} />
        <Typography variant="body1" fontSize={12} color={chatbot?.brandColor || '#3650DE'}>
          {siteName}
        </Typography>
      </Button>
    );
  }

  if (isImageLink) {
    if (chatbot?.image === true && messageContent && onOpenLinksFlyout && chatbot?.enableChatFlyout) {
      return (
        <Box
          onClick={() => {
            if (onOpenLinksFlyout && messageContent) {
              onOpenLinksFlyout(messageContent, 'image', props.href);
            }
          }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: `1px solid ${
              chatbot.brandColor ? chatbot.brandColor : '#3650DE'
            }`,
            borderRadius: '16px',
            gap: '12px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            width: 'fit-content',
          }}
        >
          <Image
            size={16}
            color={chatbot.brandColor ? chatbot.brandColor : '#3650DE'}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="body1"
              fontSize={14}
              color={chatbot.responseText ? chatbot.responseText : '#3650DE'}
            >
              IMAGE
            </Typography>

            <Typography
              variant="body1"
              fontSize={12}
              color={chatbot.pageText ? chatbot.pageText : '#fff'}
            >
              Interactive artifact
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <a href={props.href} target="_blank" rel="noopener noreferrer" {...props}>
        <img
          src={props.href}
          alt={props.children?.toString() ?? 'Image'}
          style={{
            maxWidth: '100%',
            borderRadius: '8px',
            objectFit: 'cover',
          }}
        />
      </a>
    );
  }

  if (isAudioLink) {
    if (chatbot?.audio === true && messageContent && onOpenLinksFlyout && chatbot?.enableChatFlyout) {
      return (
        <Box
          onClick={() => {
            if (onOpenLinksFlyout && messageContent) {
              onOpenLinksFlyout(messageContent, 'audio', props.href);
            }
          }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: `1px solid ${
              chatbot.brandColor ? chatbot.brandColor : '#3650DE'
            }`,
            borderRadius: '16px',
            gap: '12px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            width: 'fit-content',
          }}
        >
          <SpeakerHigh
            size={16}
            color={chatbot.brandColor ? chatbot.brandColor : '#3650DE'}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="body1"
              fontSize={14}
              color={chatbot.responseText ? chatbot.responseText : '#3650DE'}
            >
              AUDIO
            </Typography>

            <Typography
              variant="body1"
              fontSize={12}
              color={chatbot.pageText ? chatbot.pageText : '#fff'}
            >
              Interactive artifact
            </Typography>
          </Box>
        </Box>
      );
    }
    
    return (
      <audio
        ref={audioRef}
        controls
        src={props.href}
        data-id={`${props.href}-${props.id}`}
        controlsList="nodownload"
        className="my-2 w-full max-w-full"
        style={{
          maxWidth: '100%',
        }}
      ></audio>
    );
  }

  return (
    <a
      {...props}
      aria-label={props.children?.toString()}
      target="_blank"
      rel="noopener noreferrer"
    />
  );
});

export { LinkComponent };
